import { Component, OnInit } from '@angular/core';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RolePickerComponent } from 'src/app/shared/components/role-picker/role-picker.component';
import { AdminService } from 'src/app/core/services/admin.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { TenantService } from 'src/app/core/services/tenant.service';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal/confirmation-modal.component';
import { AuditHistoryModalComponent } from 'src/app/shared/components/audit-history-modal/audit-history-modal.component';
import { User } from 'src/app/core/models/user';
import {
  TableAction,
  TableColumn,
} from 'src/app/shared/models/table-config.model';
import { TableLazyLoadEvent } from 'primeng/table';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { AvailablePermission } from 'src/app/core/models/dto/availablePermission';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  standalone: false,
  providers: [DialogService],
})
export class UserManagementComponent implements OnInit {
  // Dados Brutos e Dados Visíveis
  allUsers: any[] = [];
  visibleUsers: any[] = [];
  tableData: any[] = [];
  totalRecords: number = 0;

  loading: boolean = true;
  searchText: string = '';

  ref: DynamicDialogRef | undefined;
  // Permissões locais
  canManage: boolean = false;
  isConnectSeaAdminTenant: boolean = false;

  // Configuração de Estilos para Status
  private readonly styleConfirmed = {
    label: 'Yes',
    cssClass: 'bg-green-100 text-green-700 border border-green-200 font-bold',
  };
  private readonly stylePending = {
    label: 'No',
    cssClass: 'bg-red-100 text-red-700 border border-red-200 font-bold',
  };

  // Definição das Colunas
  tableColumns: TableColumn[] = [
    {
      field: 'id',
      header: 'ID',
      type: 'text',
      width: 'auto',
      sortable: true,
      linkAction: (row) => this.openAssignmentModal(row),
    },
    {
      field: 'userName',
      header: 'USERNAME',
      type: 'text',
      width: 'auto',
      sortable: true,
    },
    {
      field: 'emailStatus',
      header: 'E-MAIL CONFIRMED?',
      type: 'status',
      width: 'auto',
      sortable: true,
      statusConfig: {
        YES: this.styleConfirmed,
        NO: this.stylePending,
      },
    },
    {
      field: 'displayRoles',
      header: 'ACTIVE ROLES',
      type: 'text',
      width: '180px',
      sortable: true,
    },
    {
      field: 'displayGroups',
      header: 'ACTIVE GROUPS',
      type: 'text',
      width: '180px',
      sortable: true,
    },
    { field: 'actions', header: '', type: 'actions', width: 'auto' },
  ];

  // Definição das Ações
  tableActions: TableAction[] = [
    {
      label: 'Manage Roles',
      icon: 'pi pi-id-card',
      visible: () => this.canManage,
      command: (row) => this.assignRolesModal(row),
    },
    {
      label: 'Manage Groups',
      icon: 'pi pi-users',
      visible: () => this.canManage,
      command: (row) => this.assignGroupsModal(row),
    },
    {
      label: 'History',
      icon: 'pi pi-history',
      styleClass: 'p-button-text p-button-rounded text-gray-500 hover:bg-gray-50',
      visible: () => this.canManage,
      command: (row) => this.openAuditHistory(row),
    },
    {
      label: 'Delete User',
      icon: 'pi pi-trash',
      styleClass: 'p-button-text p-button-danger', // Opcional se o componente suportar
      visible: () => this.canManage,
      command: (row) => this.onDeleteUser(row),
    },
  ];

  constructor(
    private adminService: AdminService,
    private authService: AuthService,
    private tenantService: TenantService,
    private readonly toastService: AppToastService,
    public dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.checkPermissions();
    this.getUsersWithRoles();
  }

  checkPermissions() {
    // Ticket #338: 'manage_user' foi deletada na migration RemoveLegacyRolesAfterPermissionMigration
    // (2026-06-19) - acesso real agora é decidido pelo back-end via Permission Groups (403 se não
    // autorizado), então o front-end não esconde mais esta ação.
    this.canManage = true;
    this.isConnectSeaAdminTenant = this.tenantService.isConnectSeaAdminTenant();
  }

  // --- Carregamento de Dados ---

  getUsersWithRoles() {
    this.loading = true;

    this.adminService
      .getUsersWithRoles()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (users: User[]) => {
          // Processamento dos dados para a tabela genérica
          this.allUsers = users.map((u) => ({
            ...u,
            displayRoles: u.roleName ? u.roleName.join(', ') : '',
            displayGroups: u.groupNames ? u.groupNames.join(', ') : '',
            // Transforma booleano em status mapeável
            emailStatus: u.emailConfirmed ? 'YES' : 'NO',
          }));

          this.totalRecords = this.allUsers.length;
          this._updateTableData();
        },
        error: () => {},
      });
  }

  // --- Lógica de Tabela (Paginação Local e Filtro) ---

  handleSearch(text: string) {
    this.searchText = text?.toLowerCase() || '';
    this._updateTableData();
  }

  private _updateTableData(): void {
    const filtered = this.searchText
      ? this.allUsers.filter((u) => this.matchesFilter(u))
      : [...this.allUsers];
    this.tableData = filtered;
  }

  loadUsersLazy(event: TableLazyLoadEvent) {
    if (!this.allUsers.length) return;
    this.processLocalData(event);
  }

  // Simula o comportamento do backend para paginação e filtro
  processLocalData(event: TableLazyLoadEvent) {
    this.loading = true;

    // 1. Filtragem
    let filtered = this.allUsers;
    if (this.searchText) {
      filtered = this.allUsers.filter((u) => this.matchesFilter(u));
    }
    this.totalRecords = filtered.length;

    // 2. Ordenação
    if (event.sortField) {
      filtered.sort((a, b) => {
        const valueA = this.resolveFieldData(a, event.sortField as string);
        const valueB = this.resolveFieldData(b, event.sortField as string);
        let result = 0;

        if (valueA == null && valueB != null) result = -1;
        else if (valueA != null && valueB == null) result = 1;
        else if (valueA == null && valueB == null) result = 0;
        else if (typeof valueA === 'string' && typeof valueB === 'string')
          result = valueA.localeCompare(valueB);
        else result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;

        return (event.sortOrder || 1) * result;
      });
    }

    // 3. Paginação
    const first = event.first || 0;
    const rows = event.rows || 10;

    // Simula delay de rede para UX
    setTimeout(() => {
      this.visibleUsers = filtered.slice(first, first + rows);
      this.loading = false;
    }, 100);
  }

  private matchesFilter(data: any): boolean {
    const searchStr = this.searchText;
    return (
      (data.userName && data.userName.toLowerCase().includes(searchStr)) ||
      String(data.id ?? '').toLowerCase().includes(searchStr) ||
      (data.displayRoles && data.displayRoles.toLowerCase().includes(searchStr)) ||
      (data.displayGroups && data.displayGroups.toLowerCase().includes(searchStr))
    );
  }

  private resolveFieldData(data: any, field: string): any {
    if (data && field) {
      if (field.indexOf('.') === -1) {
        return data[field];
      } else {
        const fields = field.split('.');
        let value = data;
        for (let i = 0; i < fields.length; ++i) {
          if (value == null) return null;
          value = value[fields[i]];
        }
        return value;
      }
    }
    return null;
  }

  // --- Ações ---

  openAuditHistory(user: any) {
    const childEntities = [
      {
        entityType: 'UserRole',
        label: 'Role',
        fieldLabels: [{ field: 'RoleId', label: 'Role' }],
        fieldEntityMap: { RoleId: 'Role' },
      },
      {
        entityType: 'UserPermissionGroup',
        label: 'Permission Group',
        fieldLabels: [{ field: 'PermissionGroupId', label: 'Group' }],
        fieldEntityMap: { PermissionGroupId: 'PermissionGroup' },
      },
    ];

    this.dialogService.open(AuditHistoryModalComponent, {
      showHeader: false,
      width: '600px',
      contentStyle: { overflow: 'visible', padding: '0', borderRadius: '12px' },
      data: {
        entityType: 'User',
        entityId: user.id,
        title: `User History — ${user.userName}`,
        // Dados sensíveis (PasswordHash, SecurityStamp, ConcurrencyStamp,
        // NormalizedEmail, NormalizedUserName) já são filtrados no AuditLogService.
        fieldLabels: [
          { field: 'Id', label: 'ID' },
          { field: 'UserName', label: 'Username' },
          { field: 'DisplayName', label: 'Display Name' },
          { field: 'Email', label: 'Email' },
          { field: 'EmailConfirmed', label: 'Email Confirmed' },
          { field: 'PhoneNumber', label: 'Phone' },
          { field: 'PhoneNumberConfirmed', label: 'Phone Confirmed' },
          { field: 'TwoFactorEnabled', label: 'Two-Factor Enabled' },
          { field: 'LockoutEnabled', label: 'Lockout Enabled' },
          { field: 'LockoutEnd', label: 'Lockout End' },
          { field: 'AccessFailedCount', label: 'Access Failed Count' },
        ],
        fieldFormatters: {
          EmailConfirmed: (v: any) =>
            v === true || v === 'true' || v === 1 ? 'Yes' : 'No',
          PhoneNumberConfirmed: (v: any) =>
            v === true || v === 'true' || v === 1 ? 'Yes' : 'No',
          TwoFactorEnabled: (v: any) =>
            v === true || v === 'true' || v === 1 ? 'Yes' : 'No',
          LockoutEnabled: (v: any) =>
            v === true || v === 'true' || v === 1 ? 'Yes' : 'No',
        },
        childEntities,
      },
    });
  }

  openAssignmentModal(user: User) {
    this.assignRolesModal(user);
  }

  assignRolesModal(user: User) {
    // Prepare initial selected (immediate) and open dialog immediately with skeleton
    const selected = (user.roleName || []).map((r) => ({
      name: r,
      label: r,
      selected: false,
    }));
    const availableInitial: any[] = [];

    this.ref = this.dialogService.open(RolePickerComponent, {
      showHeader: false,
      closable: true,
      width: '800px',
      styleClass: 'responsive-modal',
      contentStyle: {
        'max-height': '85vh',
        overflow: 'hidden',
        padding: '0',
        'border-radius': '12px',
      },
      baseZIndex: 10000,
      data: {
        title: `Edit Roles for ${user.userName}`,
        availableItems: availableInitial,
        selectedItems: selected,
      },
    });

    // When child component is loaded, mark it as loading so it can show skeleton
    let childInstance: any;
    this.ref.onChildComponentLoaded?.subscribe((comp: any) => {
      childInstance = comp as any;
      childInstance.loading = true;
      // Exibe o Name amigável (label) e mantém o PermissionKey (name) para salvar.
      childInstance.displayField = 'label';
      childInstance.idField = 'name';
      childInstance.availableItems = availableInitial;
      childInstance.selectedItems = selected;
      childInstance.normalizeInputs();
    });

    // Busca permissões (para o rótulo amigável) e roles em paralelo.
    forkJoin({
      permissions: this.adminService.getAvailableTenantPermissions(),
      roles: this.adminService.getRoles() as any,
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: ({
          permissions,
          roles,
        }: {
          permissions: AvailablePermission[];
          roles: string[];
        }) => {
          const labelByName = new Map<string, string>(
            (permissions || []).map((p) => [p.name, p.label]),
          );

          const selectedEnriched = (user.roleName || []).map((r) => ({
            name: r,
            label: labelByName.get(r) || r,
            selected: false,
          }));

          const available = (roles || [])
            .filter((r) => !user.roleName?.includes(r))
            .map((r) => ({
              name: r,
              label: labelByName.get(r) || r,
              selected: false,
            }));

          if (childInstance) {
            childInstance.displayField = 'label';
            childInstance.idField = 'name';
            childInstance.availableItems = available;
            childInstance.selectedItems = selectedEnriched;
            childInstance.normalizeInputs();
            childInstance.loading = false;
            childInstance.takeSnapshot();
          }
        },
        error: () => {
          if (childInstance) childInstance.loading = false;
        },
      });

    this.ref.onClose.subscribe((result: any) => {
      const roleNames = result?.roleNames;
      if (roleNames) {
        this.adminService.updateUserRoles(user, { roleNames }).subscribe({
          next: () => {
            user.roleName = [...roleNames];
            const index = this.allUsers.findIndex((u) => u.id === user.id);
            if (index !== -1) {
              this.allUsers[index].roleName = user.roleName;
              this.allUsers[index].displayRoles = user.roleName.join(', ');
            }
            this.handleSearch(this.searchText);
            this.toastService.success(
              'Saved',
              'User roles saved successfully.',
            );
          },
          error: () => {},
        });
      }
    });
  }

  assignGroupsModal(user: User) {
    const selected = (user.groupNames || []).map((g) => ({
      name: g,
      selected: false,
    }));

    const availableInitial: any[] = [];

    this.ref = this.dialogService.open(RolePickerComponent, {
      showHeader: false,
      closable: true,
      width: '800px',
      styleClass: 'responsive-modal',
      contentStyle: {
        'max-height': '85vh',
        overflow: 'hidden',
        padding: '0',
        'border-radius': '12px',
      },
      baseZIndex: 10000,
      data: {
        title: `Edit Groups for ${user.userName}`,
        availableItems: availableInitial,
        selectedItems: selected,
      },
    });

    let childInstance: any;
    this.ref.onChildComponentLoaded?.subscribe((comp: any) => {
      childInstance = comp as any;
      childInstance.loading = true;
      childInstance.availableItems = availableInitial;
      childInstance.selectedItems = selected;
      childInstance.normalizeInputs();
    });

    this.adminService
      .getPermissionGroups()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (groups: any[]) => {
          const available = groups
            .map((g: any) => g.name)
            .filter((name: string) => !user.groupNames?.includes(name))
            .map((name: string) => ({ name, selected: false }));

          if (childInstance) {
            childInstance.availableItems = available;
            childInstance.selectedItems = selected;
            childInstance.normalizeInputs();
            childInstance.loading = false;
            childInstance.takeSnapshot();
          }
        },
        error: () => {
          if (childInstance) childInstance.loading = false;
        },
      });

    this.ref.onClose.subscribe((result: any) => {
      const groupNames = result?.roleNames;
      if (groupNames) {
        this.adminService.updateUserPermissionGroups(String(user.id), groupNames).subscribe({
          next: () => {
            user.groupNames = [...groupNames];
            const index = this.allUsers.findIndex((u) => u.id === user.id);
            if (index !== -1) {
              this.allUsers[index].groupNames = user.groupNames;
              this.allUsers[index].displayGroups = user.groupNames.join(', ');
            }
            this.handleSearch(this.searchText);
            this.toastService.success('Saved', 'User groups saved successfully.');
          },
          error: () => {},
        });
      }
    });
  }

  onDeleteUser(user: any) {
    const ref = this.dialogService.open(ConfirmationModalComponent, {
      showHeader: false,
      width: '450px',
      contentStyle: { padding: '0', borderRadius: '8px' },
      data: {
        title: `Delete ${user.userName}?`,
        message:
          'Are you sure you want to delete this user? This action cannot be undone.',
        confirmLabel: 'Delete',
      },
    });

    ref.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.loading = true;

        this.authService.deleteUser(user.id).subscribe({
          next: () => {
            this.allUsers = this.allUsers.filter((u) => u.id !== user.id);
            this.handleSearch(this.searchText);
            this.toastService.success('Deleted', 'User has been deleted.');
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        });
      }
    });
  }
}
